/** Powered by @ttoss/config. https://ttoss.dev/docs/modules/packages/config/ */

// src/AuthProvider.tsx
import * as React from "react";
import { Auth, Hub } from "aws-amplify";
import { jsx } from "react/jsx-runtime";
var signOut = () => {
  return Auth.signOut();
};
var AuthContext = React.createContext({
  signOut,
  isAuthenticated: false,
  user: null,
  tokens: null
});
var AuthProvider = ({ children }) => {
  const [{ user, tokens, isAuthenticated }, setAuthState] = React.useState({
    user: null,
    tokens: null,
    isAuthenticated: void 0
  });
  React.useEffect(() => {
    const updateUser = () => {
      Auth.currentAuthenticatedUser().then(({ attributes, signInUserSession }) => {
        setAuthState({
          user: {
            id: attributes.sub,
            email: attributes.email,
            emailVerified: attributes["email_verified"]
          },
          tokens: {
            idToken: signInUserSession.idToken.jwtToken,
            accessToken: signInUserSession.accessToken.jwtToken,
            refreshToken: signInUserSession.refreshToken.token
          },
          isAuthenticated: true
        });
      }).catch(() => {
        setAuthState({
          user: null,
          tokens: null,
          isAuthenticated: false
        });
      });
    };
    const updateUserListener = Hub.listen("auth", updateUser);
    updateUser();
    return () => {
      updateUserListener();
    };
  }, []);
  if (isAuthenticated === void 0) {
    return null;
  }
  return /* @__PURE__ */ jsx(AuthContext.Provider, { value: { signOut, isAuthenticated, user, tokens }, children });
};
var useAuth = () => {
  return React.useContext(AuthContext);
};

// src/Auth.tsx
import * as React3 from "react";
import { Auth as AmplifyAuth } from "aws-amplify";

// src/AuthCard.tsx
import * as React2 from "react";
import { Button, Card, Flex, Link, Text } from "@ttoss/ui";
import { useNotifications } from "@ttoss/react-notifications";
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var LogoContext = React2.createContext({});
var LogoProvider = ({ children, ...values }) => {
  return /* @__PURE__ */ jsx2(LogoContext.Provider, { value: values, children });
};
var AuthCard = ({
  children,
  title,
  buttonLabel,
  links = []
}) => {
  const { logo } = React2.useContext(LogoContext);
  const { isLoading } = useNotifications();
  return /* @__PURE__ */ jsx2(Card, { sx: { maxWidth: "564px" }, children: /* @__PURE__ */ jsxs(Flex, { sx: { flexDirection: "column", gap: 3 }, children: [
    logo && /* @__PURE__ */ jsx2(Flex, { sx: { width: "100%", justifyContent: "center" }, children: logo }),
    /* @__PURE__ */ jsx2(
      Text,
      {
        variant: "title",
        sx: { alignSelf: "center", marginY: 4, fontSize: 5 },
        children: title
      }
    ),
    children,
    /* @__PURE__ */ jsx2(Flex, { sx: { justifyContent: "space-between", marginTop: 3 }, children: /* @__PURE__ */ jsx2(
      Button,
      {
        type: "submit",
        "aria-label": "submit-login",
        variant: "cta",
        disabled: isLoading,
        sx: { width: "100%" },
        children: buttonLabel
      }
    ) }),
    /* @__PURE__ */ jsx2(
      Flex,
      {
        sx: {
          justifyContent: "space-between",
          flexDirection: "column",
          gap: 3,
          marginTop: 4,
          color: "text"
        },
        children: links.map((link) => {
          return link && /* @__PURE__ */ jsx2(Link, { onClick: link.onClick, children: link.label }, link.label);
        })
      }
    )
  ] }) });
};

// src/AuthConfirmSignUp.tsx
import { Form, FormFieldInput, useForm, yup, yupResolver } from "@ttoss/forms";
import { useI18n } from "@ttoss/react-i18n";
import { jsx as jsx3 } from "react/jsx-runtime";
var AuthConfirmSignUp = ({
  email,
  onConfirmSignUp
}) => {
  const { intl } = useI18n();
  const schema = yup.object().shape({
    code: yup.string().required(
      intl.formatMessage({
        description: "Required field.",
        defaultMessage: "Required field"
      })
    ).max(
      6,
      intl.formatMessage(
        {
          description: "Minimum {value} characters.",
          defaultMessage: "Minimum {value} characters"
        },
        { value: 6 }
      )
    )
  }).required();
  const formMethods = useForm({
    resolver: yupResolver(schema)
  });
  return /* @__PURE__ */ jsx3(
    Form,
    {
      ...formMethods,
      onSubmit: ({ code }) => {
        return onConfirmSignUp({ code, email });
      },
      children: /* @__PURE__ */ jsx3(
        AuthCard,
        {
          buttonLabel: intl.formatMessage({
            description: "Confirm",
            defaultMessage: "Confirm"
          }),
          title: intl.formatMessage({
            description: "Confirmation",
            defaultMessage: "Confirmation"
          }),
          children: /* @__PURE__ */ jsx3(
            FormFieldInput,
            {
              name: "code",
              label: intl.formatMessage({
                description: "Sign up confirmation code",
                defaultMessage: "Code"
              })
            }
          )
        }
      )
    }
  );
};

// src/AuthFullScreen.tsx
import { Flex as Flex2 } from "@ttoss/ui";
import { jsx as jsx4 } from "react/jsx-runtime";
var AuthFullScreen = ({ children }) => {
  return /* @__PURE__ */ jsx4(
    Flex2,
    {
      sx: {
        height: "100vh",
        width: "100vw",
        justifyContent: "center",
        alignItems: "center",
        margin: 0,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover"
      },
      children
    }
  );
};

// src/AuthSignIn.tsx
import { Form as Form2, FormFieldInput as FormFieldInput2, useForm as useForm2, yup as yup2, yupResolver as yupResolver2 } from "@ttoss/forms";

// ../cloud-auth/dist/esm/index.js
var PASSWORD_MINIMUM_LENGTH = 8;
var CognitoUserPoolLogicalId = "CognitoUserPool";
var CognitoUserPoolClientLogicalId = "CognitoUserPoolClient";
var CognitoIdentityPoolLogicalId = "CognitoIdentityPool";
var createAuthTemplate = ({
  autoVerifiedAttributes = ["email"],
  identityPool = true,
  roles,
  schema
} = {}) => {
  const AutoVerifiedAttributes = Array.isArray(autoVerifiedAttributes) && autoVerifiedAttributes.length > 0 ? autoVerifiedAttributes : void 0;
  const template = {
    AWSTemplateFormatVersion: "2010-09-09",
    Resources: {
      [CognitoUserPoolLogicalId]: {
        Type: "AWS::Cognito::UserPool",
        Properties: {
          AutoVerifiedAttributes,
          Policies: {
            PasswordPolicy: {
              MinimumLength: PASSWORD_MINIMUM_LENGTH,
              RequireLowercase: false,
              RequireNumbers: false,
              RequireSymbols: false,
              RequireUppercase: false,
              TemporaryPasswordValidityDays: 30
            }
          },
          Schema: schema,
          UsernameAttributes: ["email"],
          UsernameConfiguration: {
            CaseSensitive: false
          },
          UserPoolName: {
            Ref: "AWS::StackName"
          }
        }
      },
      [CognitoUserPoolClientLogicalId]: {
        Type: "AWS::Cognito::UserPoolClient",
        Properties: {
          SupportedIdentityProviders: ["COGNITO"],
          UserPoolId: {
            Ref: "CognitoUserPool"
          }
        }
      }
    },
    Outputs: {
      Region: {
        Description: "You use this value on Amplify Auth `region`.",
        Value: {
          Ref: "AWS::Region"
        },
        Export: {
          Name: {
            "Fn::Join": [":", [{ Ref: "AWS::StackName" }, "Region"]]
          }
        }
      },
      UserPoolId: {
        Description: "You use this value on Amplify Auth `userPoolId`.",
        Value: {
          Ref: CognitoUserPoolLogicalId
        },
        Export: {
          Name: {
            "Fn::Join": [":", [{ Ref: "AWS::StackName" }, "UserPoolId"]]
          }
        }
      },
      AppClientId: {
        Description: "You use this value on Amplify Auth `userPoolWebClientId`.",
        Value: {
          Ref: CognitoUserPoolClientLogicalId
        },
        Export: {
          Name: {
            "Fn::Join": [":", [{ Ref: "AWS::StackName" }, "AppClientId"]]
          }
        }
      }
    }
  };
  if (identityPool) {
    template.Resources[CognitoIdentityPoolLogicalId] = {
      Type: "AWS::Cognito::IdentityPool",
      Properties: {
        AllowUnauthenticatedIdentities: true,
        CognitoIdentityProviders: [
          {
            ClientId: {
              Ref: CognitoUserPoolClientLogicalId
            },
            ProviderName: {
              "Fn::GetAtt": [CognitoUserPoolLogicalId, "ProviderName"]
            }
          }
        ]
      }
    };
    if (roles) {
      template.Resources.CognitoIdentityPoolRoleAttachment = {
        Type: "AWS::Cognito::IdentityPoolRoleAttachment",
        Properties: {
          IdentityPoolId: {
            Ref: CognitoIdentityPoolLogicalId
          },
          Roles: roles
        }
      };
    }
    if (!template.Outputs) {
      template.Outputs = {};
    }
    template.Outputs.IdentityPoolId = {
      Description: "You use this value on Amplify Auth `identityPoolId`.",
      Value: {
        Ref: CognitoIdentityPoolLogicalId
      },
      Export: {
        Name: {
          "Fn::Join": [
            ":",
            [{ Ref: "AWS::StackName" }, "CognitoIdentityPoolId"]
          ]
        }
      }
    };
  }
  return template;
};
createAuthTemplate.CognitoUserPoolLogicalId = CognitoUserPoolLogicalId;
createAuthTemplate.CognitoUserPoolClientLogicalId = CognitoUserPoolClientLogicalId;
createAuthTemplate.CognitoIdentityPoolLogicalId = CognitoIdentityPoolLogicalId;

// src/AuthSignIn.tsx
import { useI18n as useI18n2 } from "@ttoss/react-i18n";
import { jsx as jsx5, jsxs as jsxs2 } from "react/jsx-runtime";
var AuthSignIn = ({
  onSignIn,
  onSignUp,
  defaultValues
}) => {
  const { intl } = useI18n2();
  const schema = yup2.object().shape({
    email: yup2.string().required(
      intl.formatMessage({
        description: "Email is a required field.",
        defaultMessage: "Email field is required"
      })
    ).email(
      intl.formatMessage({
        description: "Invalid email.",
        defaultMessage: "Invalid email"
      })
    ),
    password: yup2.string().required(
      intl.formatMessage({
        description: "Password is required.",
        defaultMessage: "Password field is required"
      })
    ).min(
      PASSWORD_MINIMUM_LENGTH,
      intl.formatMessage(
        {
          description: "Password must be at least {value} characters long.",
          defaultMessage: "Password requires {value} characters"
        },
        { value: PASSWORD_MINIMUM_LENGTH }
      )
    ).trim()
  });
  const formMethods = useForm2({
    defaultValues,
    resolver: yupResolver2(schema)
  });
  const onSubmitForm = (data) => {
    return onSignIn(data);
  };
  return /* @__PURE__ */ jsx5(Form2, { ...formMethods, onSubmit: onSubmitForm, children: /* @__PURE__ */ jsxs2(
    AuthCard,
    {
      title: intl.formatMessage({
        description: "Sign in title.",
        defaultMessage: "Login"
      }),
      buttonLabel: intl.formatMessage({
        description: "Button label.",
        defaultMessage: "Login"
      }),
      links: [
        {
          onClick: onSignUp,
          label: intl.formatMessage({
            description: "Link to retrieve password.",
            defaultMessage: "Do you forgot your password?"
          })
        },
        {
          onClick: onSignUp,
          label: intl.formatMessage({
            description: "Link to sign up.",
            defaultMessage: "Don't have an account? Sign up"
          })
        }
      ],
      children: [
        /* @__PURE__ */ jsx5(
          FormFieldInput2,
          {
            name: "email",
            label: intl.formatMessage({
              description: "Email label.",
              defaultMessage: "Email"
            })
          }
        ),
        /* @__PURE__ */ jsx5(
          FormFieldInput2,
          {
            name: "password",
            label: intl.formatMessage({
              description: "Password label.",
              defaultMessage: "Password"
            })
          }
        )
      ]
    }
  ) });
};

// src/AuthSignUp.tsx
import { Form as Form3, FormFieldInput as FormFieldInput3, useForm as useForm3, yup as yup3, yupResolver as yupResolver3 } from "@ttoss/forms";
import { useI18n as useI18n3 } from "@ttoss/react-i18n";
import { jsx as jsx6, jsxs as jsxs3 } from "react/jsx-runtime";
var AuthSignUp = ({ onSignUp, onReturnToSignIn }) => {
  const { intl } = useI18n3();
  const schema = yup3.object().shape({
    email: yup3.string().required(
      intl.formatMessage({
        description: "Email is a required field.",
        defaultMessage: "Email field is required"
      })
    ).email(
      intl.formatMessage({
        description: "Invalid email.",
        defaultMessage: "Invalid email"
      })
    ),
    password: yup3.string().required(
      intl.formatMessage({
        description: "Password is required.",
        defaultMessage: "Password field is required"
      })
    ).min(
      PASSWORD_MINIMUM_LENGTH,
      intl.formatMessage(
        {
          description: "Password must be at least {value} characters long.",
          defaultMessage: "Password requires {value} characters"
        },
        { value: PASSWORD_MINIMUM_LENGTH }
      )
    ).trim()
  });
  const formMethods = useForm3({
    resolver: yupResolver3(schema)
  });
  const onSubmitForm = (data) => {
    return onSignUp(data);
  };
  return /* @__PURE__ */ jsx6(Form3, { ...formMethods, onSubmit: onSubmitForm, children: /* @__PURE__ */ jsxs3(
    AuthCard,
    {
      buttonLabel: intl.formatMessage({
        description: "Create account.",
        defaultMessage: "Create account"
      }),
      title: intl.formatMessage({
        description: "Title on sign up.",
        defaultMessage: "Register"
      }),
      links: [
        {
          label: intl.formatMessage({
            description: "Link to sign in on sign up.",
            defaultMessage: "Do you already have an account? Sign in"
          }),
          onClick: onReturnToSignIn
        }
      ],
      children: [
        /* @__PURE__ */ jsx6(
          FormFieldInput3,
          {
            name: "email",
            label: intl.formatMessage({
              description: "Email label.",
              defaultMessage: "Email"
            })
          }
        ),
        /* @__PURE__ */ jsx6(
          FormFieldInput3,
          {
            name: "password",
            label: intl.formatMessage({
              description: "Password label.",
              defaultMessage: "Password"
            })
          }
        )
      ]
    }
  ) });
};

// src/Auth.tsx
import { assign, createMachine } from "xstate";
import { useMachine } from "@xstate/react";
import { useNotifications as useNotifications2 } from "@ttoss/react-notifications";
import { jsx as jsx7 } from "react/jsx-runtime";
var authMachine = createMachine(
  {
    predictableActionArguments: true,
    initial: "signIn",
    states: {
      signIn: {
        on: {
          SIGN_UP: { target: "signUp" },
          SIGN_UP_RESEND_CONFIRMATION: {
            actions: ["assignEmail"],
            target: "signUpConfirm"
          }
        }
      },
      signUp: {
        on: {
          SIGN_UP_CONFIRM: {
            actions: ["assignEmail"],
            target: "signUpConfirm"
          },
          RETURN_TO_SIGN_IN: { target: "signIn" }
        }
      },
      signUpConfirm: {
        on: {
          SIGN_UP_CONFIRMED: {
            actions: ["assignEmail"],
            target: "signIn"
          }
        }
      }
    }
  },
  {
    actions: {
      assignEmail: assign({
        email: (_, event) => {
          return event.email;
        }
      })
    }
  }
);
var AuthLogic = () => {
  const { isAuthenticated } = useAuth();
  const [state, send] = useMachine(authMachine);
  const { setLoading } = useNotifications2();
  const onSignIn = React3.useCallback(
    async ({ email, password }) => {
      try {
        setLoading(true);
        await AmplifyAuth.signIn(email, password);
      } catch (error) {
        switch (error.code) {
          case "UserNotConfirmedException":
            await AmplifyAuth.resendSignUp(email);
            send({ type: "SIGN_UP_RESEND_CONFIRMATION", email });
            break;
          default:
        }
      } finally {
        setLoading(false);
      }
    },
    [send, setLoading]
  );
  const onSignUp = React3.useCallback(
    async ({ email, password }) => {
      try {
        setLoading(true);
        await AmplifyAuth.signUp({
          username: email,
          password,
          attributes: { email }
        });
        send({ type: "SIGN_UP_CONFIRM", email });
      } catch (error) {
      } finally {
        setLoading(false);
      }
    },
    [send, setLoading]
  );
  const onConfirmSignUp = React3.useCallback(
    async ({ email, code }) => {
      try {
        setLoading(true);
        await AmplifyAuth.confirmSignUp(email, code);
        send({ type: "SIGN_UP_CONFIRMED", email });
      } catch (error) {
      } finally {
        setLoading(false);
      }
    },
    [send, setLoading]
  );
  const onReturnToSignIn = React3.useCallback(() => {
    send({ type: "RETURN_TO_SIGN_IN" });
  }, [send]);
  if (isAuthenticated) {
    return null;
  }
  if (state.matches("signUp")) {
    return /* @__PURE__ */ jsx7(AuthSignUp, { onSignUp, onReturnToSignIn });
  }
  if (state.matches("signUpConfirm")) {
    return /* @__PURE__ */ jsx7(
      AuthConfirmSignUp,
      {
        onConfirmSignUp,
        email: state.context.email
      }
    );
  }
  return /* @__PURE__ */ jsx7(
    AuthSignIn,
    {
      onSignIn,
      onSignUp: () => {
        return send("SIGN_UP");
      },
      defaultValues: { email: state.context.email }
    }
  );
};
var Auth2 = ({ logo, fullScreen = true }) => {
  const withLogoNode = React3.useMemo(() => {
    return /* @__PURE__ */ jsx7(LogoProvider, { logo, children: /* @__PURE__ */ jsx7(AuthLogic, {}) });
  }, [logo]);
  if (fullScreen) {
    return /* @__PURE__ */ jsx7(AuthFullScreen, { children: withLogoNode });
  }
  return withLogoNode;
};
export {
  Auth2 as Auth,
  AuthProvider,
  useAuth
};
